import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { RapidStorUnitAvailability } from '@r6digital/rapidstor-types/types/Api';
import { JourneyVariant } from '@r6digital/rapidstor-types/react/entities/Journey';

import { useGetUnitAvailabilityMutation } from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';
import { fadeOut } from '@/core/utils/ui';

import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

interface UseUnitAvailabilityParams {
    location: Location;
    reservation?: BaseReservation;
    onUnitAvailable?: (unitAvailability: RapidStorUnitAvailability) => void;
    onUnitUnavailable?: () => void;
    onError?: (error: unknown) => void;
    eventName?: string; // Custom event name for analytics
    errorContext?: string; // Context for error logging
}

interface UseUnitAvailabilityReturn {
    handleUnitAvailability: () => Promise<boolean>;
    checkUnitAvailability: (reservationId: string) => Promise<RapidStorUnitAvailability>;
    isLoading: boolean;
}

export const useUnitAvailability = ({
    location,
    reservation,
    onUnitAvailable,
    onUnitUnavailable,
    onError,
    eventName = 'unit_unavailable_for_move_in',
    errorContext = 'processBooking',
}: UseUnitAvailabilityParams): UseUnitAvailabilityReturn => {
    const navigate = useNavigate();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { setUnavailableDescriptorIds, unavailableDescriptorIds } = useContext(JourneyContext);
    const [getUnitAvailability, { isLoading }] = useGetUnitAvailabilityMutation();

    const checkUnitAvailability = useCallback(async (
        reservationId: string,
    ): Promise<RapidStorUnitAvailability> => {
        const result = await getUnitAvailability({
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            reservationId,
        }).unwrap();

        return result;
    }, [getUnitAvailability, location.locationCode, location.corpCode]);

    const handleUnitAvailability = useCallback(async (): Promise<boolean> => {
        try {
            if (reservation) {
                const unitAvailabilityRes = await checkUnitAvailability(reservation._id);

                if (!unitAvailabilityRes.unit) {
                    firebaseLogEvent(eventName, {});
                    setUnavailableDescriptorIds((previous) => {
                        const descriptorId = reservation.unitType.descriptor._id;
                        return previous.includes(descriptorId) ?
                            previous :
                            [
                                ...previous,
                                descriptorId,
                            ];
                    });

                    if (onUnitUnavailable) {
                        onUnitUnavailable();
                    } else {
                        setTimeout(() => {
                            if (
                                window.rapidstor_settings.app_config.journey.variant === JourneyVariant.STANDARD_UNIT_LISTING
                            ) {
                                navigate('/', {
                                    state: {
                                        unitUnavailableAtMoveIn: true,
                                        unavailableDescriptor: reservation.unitType.descriptor._id,
                                    },
                                });
                            } else {
                                fadeOut();
                                navigate('/unit-listing', {
                                    state: {
                                        unitUnavailableAtMoveIn: true,
                                        unavailableDescriptor: reservation.unitType.descriptor._id,
                                    },
                                });
                            }
                        }, 300);
                    }
                    return false;
                }

                // Check if we need to update the local reservation with the new unit
                if (unitAvailabilityRes.updated) {
                    onUnitAvailable?.(unitAvailabilityRes);
                }
                return true;
            }

            return false;
        } catch (error) {
            Logger.error(
                `[${errorContext}] Failed to process unit availability request: ${JSON.stringify(
                    error,
                )}`,
                location,
                [
                    {
                        name: 'rapidstor_reservationToken',
                        value: `${reservation?.token}`,
                    },
                ],
            );
            onError?.(error);
            return false;
        }
    }, [
        reservation,
        checkUnitAvailability,
        firebaseLogEvent,
        setUnavailableDescriptorIds,
        unavailableDescriptorIds,
        onUnitAvailable,
        onUnitUnavailable,
        onError,
        navigate,
        location,
        eventName,
        errorContext,
    ]);

    return {
        handleUnitAvailability,
        checkUnitAvailability,
        isLoading,
    };
};

// Convenience function for move-in usage
export const useMoveInUnitAvailability = (
    location: Location,
    reservation: BaseReservation | undefined,
    setReservation: (reservation: BaseReservation) => void,
    setMoveInError: (error: string) => void,
    setMoveInLoading: (loading: boolean) => void,
) => useUnitAvailability({
        location,
        reservation,
        eventName: 'unit_unavailable_for_move_in',
        errorContext: 'processBooking',
        onUnitAvailable: (unitAvailability) => {
            if (unitAvailability.updated) {
                setReservation(unitAvailability.reservation);
            }
        },
        onError: (error) => {
            setMoveInError(`Move-in failed: ${JSON.stringify(error)}`);
            setMoveInLoading(false);
        },
    });

// Convenience function for reservation-with-fee usage
export const useReservationWithFeeUnitAvailability = (
    location: Location,
    reservation: BaseReservation | undefined,
    setReservation: (reservation: BaseReservation) => void,
    setAddFeeError: (error: string) => void,
    setSubmittingPayment: (loading: boolean) => void,
) => useUnitAvailability({
        location,
        reservation,
        eventName: 'unit_unavailable_for_reservation_fee',
        errorContext: 'processBooking',
        onUnitAvailable: (unitAvailability) => {
            if (unitAvailability.updated) {
                setReservation(unitAvailability.reservation);
            }
        },
        onError: (error) => {
            setAddFeeError(`Reservation Fee payment failed: ${JSON.stringify(error)}`);
            setSubmittingPayment(false);
        },
    });
